import { type DepartmentApiModel } from '@webapp/core/data-access/api-models/DepartmentApiModel'
import { type LocationApiModel } from '@webapp/core/data-access/api-models/LocationApiModel'
import { mapDepartment } from '@webapp/core/data-access/mappers/mapDepartment'
import { mapLocation } from '@webapp/core/data-access/mappers/mapLocation'
import { type PrimaryCategoryConstructor } from '@webapp/core/entities/internal/PrimaryCategory'
import { type SecondaryCategoryConstructor } from '@webapp/core/entities/internal/SecondaryCategory'
import { type ApiMetadata } from '@webapp/core/interfaces/api/ApiMetadata'
import { type ApiPagination } from '@webapp/core/interfaces/api/ApiPagination'
import { type ApiResponse } from '@webapp/core/interfaces/api/ApiResponse'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IRequestableCategoryRepository } from '../../../business-logic/interfaces/IRequestableCategoryRepository'

interface Dependency {
  httpClient: IHttpClient
}

export class RequestableCategoryRepository implements IRequestableCategoryRepository {
  constructor(private readonly deps: Dependency) {}

  async listRequestableLocations(
    params: RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<PrimaryCategoryConstructor>> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<LocationApiModel[], ApiMetadata<ApiPagination>>
      >(`/api/v3/locations/`, {
        params: {
          requestable: 'EXPENSE',
          ...formatRepoFiltersToApiFilters(params),
        },
      })

      const metadata = res.data.metadata

      return {
        data: res.data.data.map(mapLocation),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listRequestableDepartments(
    params: { branchId: number } & RepoFilterParams
  ): Promise<RepoPaginatedResponse<SecondaryCategoryConstructor>> {
    const { branchId, ...filterParams } = params

    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<DepartmentApiModel[], ApiMetadata<ApiPagination>>
      >(`/api/v3/departments/`, {
        params: {
          branch: branchId,
          requestable: 'EXPENSE',
          ...formatRepoFiltersToApiFilters(filterParams),
        },
      })

      const metadata = res.data.metadata

      return {
        data: res.data.data.map(mapDepartment),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
